import React, { useState } from 'react';
import { NavBar, Link, DraftsIcon, RecentIcon, ArrowDownIcon, ArrowUpIcon, Dropdown, DropdownLink, CogIcon, } from '@miroculus/nucleo';
import { ACCOUNT_SETTINGS_URL, DRAFTS_URL, LOGOUT_URL, RECENT_URL, organizationUrl, teamUrl, } from 'cons/routes';
import classnames from 'classnames/bind';
import { useSelector } from 'react-redux';
import apiClient from '@miroculus/api-client';
import { matchPath, useLocation } from 'react-router-dom';
import Collapsible from '../Collapsible/Collapsible';
import { getOrganizations, getLoadingOrganizations, } from 'reduxModules/organizations/selectors';
import { getLoading } from 'reduxModules/auth/selectors';
import { userPlaceholder } from 'images';
import styles from './Sidebar.scss';
const cx = classnames.bind(styles);
const APP_VERSION = process.env.APP_VERSION || 'dev';
const UserAccountDropdownActions = () => (React.createElement(Dropdown, { button: React.createElement(ArrowDownIcon, null), align: "right" },
    React.createElement(DropdownLink, { to: ACCOUNT_SETTINGS_URL }, "Account Settings"),
    React.createElement(DropdownLink, { to: LOGOUT_URL }, "Logout"),
    React.createElement("div", { className: cx('userAccountDropdownVersion'), "data-testid": "appVersion" },
        "v",
        APP_VERSION)));
const UserAccountBrief = ({ userName, email }) => (React.createElement("div", { className: cx('userAccountBrief'), "data-testid": "userAccountBrief" },
    React.createElement("div", { className: cx('userAccountBriefAvatar') },
        React.createElement("div", { className: cx('userAccountBriefAvatarImage') },
            React.createElement("img", { src: userPlaceholder, alt: "Avatar" }))),
    React.createElement("div", { className: cx('userAccountBriefInfo') },
        React.createElement("p", { "data-testid": "navBarUserName", className: cx('userAccountBriefInfoUserName') }, userName),
        React.createElement("p", { "data-testid": "navBarUserEmail", className: cx('userAccountBriefInfoEmail') }, email)),
    React.createElement(UserAccountDropdownActions, null)));
const OrganizationTeams = ({ teams, pathname }) => (React.createElement("ul", { className: cx('organizationTeamsList') }, teams.map(({ id: teamId, title }) => {
    const teamMatch = matchPath({ path: teamUrl(teamId), end: false }, pathname);
    const isSelected = !!teamMatch;
    return (React.createElement("li", { className: cx({ selected: isSelected }), key: teamId },
        React.createElement(Link, { to: teamUrl(teamId), "aria-current": isSelected, "data-testid": `team-button-${title}` }, title)));
})));
const Shortcuts = ({ recentSelected = false, draftsSelected = false }) => (React.createElement("ul", { className: cx('shortcutsList', 'navBarBlock') },
    React.createElement("li", { className: cx({ selected: recentSelected }) },
        React.createElement(Link, { to: RECENT_URL, "aria-current": recentSelected },
            React.createElement(RecentIcon, null),
            "Recent")),
    React.createElement("li", { className: cx({ selected: draftsSelected }) },
        React.createElement(Link, { to: DRAFTS_URL, "aria-current": draftsSelected },
            React.createElement(DraftsIcon, null),
            "Drafts"))));
const OrganizationList = ({ organizations, pathname, collapsedState, onCollapsedStateChange, }) => (React.createElement("ul", { className: cx('organizationsList') }, organizations
    ?.filter(({ name }) => !!name)
    .map(({ name, teams, slug }) => {
    const organizationName = name ?? 'No Organization';
    const match = matchPath({ path: organizationUrl(':orgSlug'), end: true }, pathname);
    const selected = match?.params?.orgSlug === slug;
    const handleCollapsibleChange = (open) => {
        if (slug) {
            onCollapsedStateChange({ ...collapsedState, [slug]: open });
        }
    };
    return (React.createElement("li", { className: cx('organizationsListItem', 'navBarBlock'), key: organizationName, "data-testid": `organization-link-${organizationName}` },
        React.createElement(Link, { className: cx('organizationsListItemLink', { selected }), "aria-current": selected, to: organizationUrl(slug), "data-testid": `sidebar-org-${slug}` }, organizationName),
        React.createElement(Collapsible, { "data-testid": `organization-collapsible-${slug}`, header: teams?.length
                ? (expanded) => expanded ? React.createElement(ArrowDownIcon, null) : React.createElement(ArrowUpIcon, null)
                : () => null, open: slug ? collapsedState[slug] : false, onChange: handleCollapsibleChange },
            React.createElement(OrganizationTeams, { teams: teams, pathname: pathname }))));
})));
/**
 * Render the app Sidebar with Recent view and Drafts view links and also lists
 * User Organizations and its Teams. The header of the Sidebar contains some
 * User information and actions for logout and show the Account Settings modal
 */
export const Sidebar = ({ open }) => {
    const userName = useSelector((state) => state.auth.user.name);
    const email = useSelector((state) => state.auth.user.email);
    const organizations = useSelector(getOrganizations);
    const loadingOrganizations = useSelector(getLoadingOrganizations);
    const loading = useSelector(getLoading);
    const { pathname } = useLocation();
    const recentMatch = matchPath({ path: RECENT_URL, end: true }, pathname);
    const rootMatch = matchPath({ path: '/', end: true }, pathname);
    const draftsMatch = matchPath({ path: DRAFTS_URL, end: true }, pathname);
    const [collapsedState, setCollapsedState] = useState(apiClient.storage.get('sidebar-state') || {});
    const handleCollapsedStateChange = (newState) => {
        setCollapsedState(newState);
        apiClient.storage.set('sidebar-state', newState);
    };
    const showBlankState = !loading && !loadingOrganizations && !organizations?.length;
    return (React.createElement("div", { className: cx('container', { open }), "data-testid": "sidebar" },
        React.createElement(NavBar, null,
            React.createElement(UserAccountBrief, { userName: userName, email: email }),
            React.createElement("div", { className: cx('navBarActions') },
                React.createElement(Shortcuts, { recentSelected: !!recentMatch || !!rootMatch, draftsSelected: !!draftsMatch }),
                React.createElement(OrganizationList, { organizations: organizations, pathname: pathname, collapsedState: collapsedState, onCollapsedStateChange: handleCollapsedStateChange }),
                showBlankState && (React.createElement("div", { className: cx('organizationsListEmpty') },
                    React.createElement(CogIcon, null),
                    React.createElement("p", null, "No organization has been added yet.")))))));
};
export default Sidebar;
